import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/config";
import { Clock, Users, MapPin, Check, X, ArrowRight, Maximize2, BedDouble, Plane } from "lucide-react";
import Lightbox from "@/components/Lightbox";
import EnquiryBadge from "@/components/EnquiryBadge";
import CompareToggle from "@/components/CompareToggle";

export default function TourDetailPage() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/tours/${slug}`).then((r) => setTour(r.data)).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return <div className="pt-36 text-center text-[#23170E]/55">Loading…</div>;
  if (!tour) return <div className="pt-36 text-center">Tour not found.</div>;

  const images = [{ url: tour.hero_image, caption: tour.summary }, ...(tour.gallery || [])];

  return (
    <div data-testid="tour-detail-page" className="bg-[#FAF6F0]">
      {/* Hero */}
      <section className="relative h-[75vh] min-h-[560px] overflow-hidden">
        <img src={tour.hero_image} alt={tour.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/75" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 pt-32 h-full flex flex-col justify-end text-[#FAF6F0]">
          <p className="overline" style={{ color: "#C88B35" }}>{tour.park}</p>
          <h1 data-testid="tour-title" className="font-display text-5xl md:text-7xl italic font-light mt-3 leading-[1.02] max-w-4xl text-[#FAF6F0]">{tour.title}</h1>
          {tour.subtitle && <p className="font-marcellus text-lg md:text-2xl mt-3 text-[#FAF6F0]/85">{tour.subtitle}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-6 text-sm">
            <span className="flex items-center gap-2"><Clock size={16} /> {tour.duration_label}</span>
            <span className="flex items-center gap-2"><Users size={16} /> {tour.group_size} guests</span>
            <span className="flex items-center gap-2"><MapPin size={16} /> {tour.difficulty}</span>
            <EnquiryBadge count={tour.enquiries_last_7d} slug={tour.slug} variant="hero" />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
          {/* Main */}
          <div className="md:col-span-8">
            <p className="overline">overview</p>
            <p className="mt-4 text-xl md:text-2xl leading-relaxed font-display italic font-light text-[#23170E]">{tour.summary}</p>

            {/* Highlights */}
            <div className="mt-14">
              <h2 className="font-display text-4xl italic font-light">Highlights</h2>
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {tour.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-[#23170E]">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#A6521B] flex-shrink-0" />
                    <span className="leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Itinerary */}
            <div className="mt-16">
              <h2 className="font-display text-4xl italic font-light">Day-by-day</h2>
              <div className="mt-8 border-l border-[#BCA993]">
                {tour.itinerary.map((d) => (
                  <div key={d.day} data-testid={`itinerary-day-${d.day}`} className="pl-8 pb-10 relative">
                    <div className="absolute -left-[7px] top-2 w-3 h-3 rounded-full bg-[#A6521B]" />
                    <p className="overline">Day {d.day}</p>
                    <h3 className="font-marcellus text-2xl mt-2">{d.title}</h3>
                    {d.route && (
                      <p className="mt-1.5 text-xs uppercase tracking-[0.16em] text-[#A6521B] flex items-center gap-2">
                        <MapPin size={12} /> {d.route}
                      </p>
                    )}
                    <p className="mt-3 text-[#23170E]/65 leading-relaxed">{d.description}</p>
                    {d.stay && (
                      <p data-testid={`itinerary-stay-${d.day}`} className="mt-4 inline-flex items-center gap-2 text-xs bg-[#23170E]/6 rounded-full px-4 py-2">
                        <BedDouble size={13} className="text-[#A6521B]" /> Overnight: <strong className="font-medium">{d.stay}</strong>
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {tour.itinerary_note && (
                <p data-testid="itinerary-note" className="text-xs text-[#23170E]/55 leading-relaxed border-l-2 border-[#A6521B]/40 pl-4">
                  {tour.itinerary_note}
                </p>
              )}
              {tour.flight_note && (
                <p data-testid="flight-note" className="mt-4 text-xs text-[#23170E]/70 leading-relaxed bg-[#A6521B]/8 rounded-2xl p-4 flex items-start gap-3">
                  <Plane size={15} className="text-[#A6521B] mt-0.5 flex-shrink-0" /> {tour.flight_note}
                </p>
              )}
            </div>

            {/* Where you'll stay */}
            {tour.stays?.length > 0 && (
              <div className="mt-16" data-testid="stays-section">
                <h2 className="font-display text-4xl italic font-light">Where you'll stay</h2>
                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  {tour.stays.map((s) => (
                    <div key={s.name} data-testid={`stay-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] overflow-hidden">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover img-hover" />
                      </div>
                      <div className="p-5">
                        <p className="overline">{s.nights}</p>
                        <h3 className="font-marcellus text-xl mt-1.5 leading-tight">{s.name}</h3>
                        {s.note && <p className="mt-2 text-sm text-[#23170E]/60 leading-relaxed">{s.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
                {tour.stay_note && (
                  <p data-testid="stay-note" className="mt-5 text-xs text-[#23170E]/55 leading-relaxed border-l-2 border-[#A6521B]/40 pl-4">
                    {tour.stay_note}
                  </p>
                )}
              </div>
            )}

            {/* Gallery */}
            <div className="mt-16">
              <div className="flex justify-between items-end mb-6">
                <h2 className="font-display text-4xl italic font-light">Glimpses</h2>
                <button
                  data-testid="open-gallery-btn"
                  onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                  className="text-xs uppercase tracking-[0.18em] text-[#A6521B] hover:text-[#8C4113] flex items-center gap-2"
                >
                  View all <Maximize2 size={14} />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {images.slice(0, 6).map((img, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                    className={`relative overflow-hidden group rounded-2xl ${i === 0 ? "col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}
                  >
                    <img src={img.url} alt={img.caption || `${tour.title} — ${i + 1}`} className="w-full h-full object-cover img-hover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <Maximize2 size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Inc/Exc */}
            <div className="mt-16 grid md:grid-cols-2 gap-10">
              <div>
                <h3 className="font-marcellus text-2xl mb-5">What's included</h3>
                <ul className="space-y-3">
                  {tour.inclusions.map((i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#23170E]">
                      <Check size={18} className="text-[#A6521B] mt-0.5 flex-shrink-0" /> {i}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-marcellus text-2xl mb-5">Not included</h3>
                <ul className="space-y-3">
                  {tour.exclusions.map((i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#23170E]/60">
                      <X size={18} className="text-[#A6352B] mt-0.5 flex-shrink-0" /> {i}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar booking card */}
          <aside className="md:col-span-4">
            <div className="sticky top-28 bg-[#FFFFFF] rounded-3xl p-8 border border-[#E2D5C3] shadow-lg shadow-[#23170E]/5">
              <p className="overline">{tour.duration_label}</p>
              <h3 className="font-marcellus text-3xl text-[#23170E] mt-2 leading-tight">{tour.subtitle || tour.title}</h3>
              <p className="text-sm text-[#23170E]/65 mt-3 leading-relaxed">Tell us your dates and group size — we'll reply within 24 hours with a tailored itinerary and quote.</p>

              <Link
                to={`/booking?tour=${tour.slug}`}
                data-testid="book-this-tour-btn"
                className="mt-6 w-full btn-pill btn-pill-primary justify-center"
              >
                Book Now <ArrowRight size={14} />
              </Link>

              <CompareToggle slug={tour.slug} title={tour.title} variant="detail" />

              <div className="mt-8 pt-6 border-t border-[#E2D5C3] space-y-2 text-xs text-[#23170E]/65">
                <p>✓ No payment required to book</p>
                <p>✓ Private guided tour, tailored to you</p>
                <p>✓ Mid-range, luxury & premium lodges available</p>
                <p>✓ Personalized response within 24h</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Lightbox images={images} open={lightboxOpen} index={lightboxIndex} onClose={() => setLightboxOpen(false)} />
    </div>
  );
}
